<template>
  <div class="workspace-single-upload-form">
    <div class="title-panel">
      <h3>{{ t('workspaceDocuments.singleUploadTitle') }}</h3>
    </div>
    <loading-wrapper :is-loading="!contentTypeFields.length">
      <single-upload-form
        :content-type-fields="contentTypeFields"
        :content-type-id="contentTypeId"
        :display-document-id-field="false"
        :is-busy="isUploading"
        :is-uploading="isUploading"
        :is-rendition-file-required="false"
        :no-required-fields="false"
        :upload-progress="uploadProgress"
        @form-submit="handleFormSubmit"
        @cancel="goToWorkspaceDetails"
      >
        <template #additional-error-messages>
          <message-wrapper v-if="kksAuthorizationError" severity="error">
            {{ kksAuthorizationError }}
          </message-wrapper>
        </template>
      </single-upload-form>
    </loading-wrapper>
  </div>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';
  import { useGetContentTypeFields } from '@/composables/api/content-types/use-get-content-type-fields';
  import { useWorkspaceUpload } from '@/composables/api/workspaces/useWorkspaceUpload';
  import { Route, routeParams } from '@/routes';
  import { WorkspaceDetailsTab } from '@/views/WorkspaceDetails/WorkspaceDetails.types';
  import { SingleUploadFormSubmitEvent } from '@/components/SingleUploadForm/SingleUploadForm.types';
  import SingleUploadForm from '@/components/SingleUploadForm/SingleUploadForm.vue';
  import { useToast } from '@/composables/useToast';
  import {
    DEFAULT_METADATA_VALUES_SEPARATOR,
    IFileMetadata,
    UniversalMappingField,
  } from 'types';
  import MessageWrapper from '@/components/MessageWrapper/MessageWrapper.vue';
  import { useWorkspaceKks } from '@/composables/api/workspaces/useWorkspaceKks';

  const props = defineProps<{
    workspaceId: string;
    contentTypeId: string;
  }>();

  const { t } = useI18n();
  const router = useRouter();
  const { createSuccessToast, createErrorToast } = useToast();

  const isUploading = ref(false);
  const kksAuthorizationError = ref<string | null>(null);

  const { contentTypeFields } = useGetContentTypeFields(
    props.contentTypeId,
    true,
  );
  const { uploadDocument, uploadProgress } = useWorkspaceUpload(
    props.workspaceId,
    props.contentTypeId,
  );
  const { validateKksAccess } = useWorkspaceKks();

  async function handleFormSubmit(
    event: SingleUploadFormSubmitEvent,
  ): Promise<void> {
    if (isUploading.value) return;
    try {
      kksAuthorizationError.value = null;
      const kks = getKksCodes(event.formEvent.values);
      if (kks.length) {
        const isAuthorized = await validateKksAccess(props.workspaceId, kks);
        if (!isAuthorized) {
          kksAuthorizationError.value = t(
            'workspaceDocuments.errors.kksNotInScope',
          );
          return;
        }
      }
      isUploading.value = true;
      await uploadDocument(
        event.formEvent.values as IFileMetadata,
        event.mainFile,
        event.renditionFile,
      );
      createSuccessToast(t('documentUpload.uploadSuccessfull', 1));
      goToWorkspaceDetails();
    } catch {
      createErrorToast(t('documentUpload.errors.uploadFailed'));
    } finally {
      isUploading.value = false;
    }
  }

  function getKksCodes(metadata: Record<string, unknown>): string[] {
    const rawKks = metadata[UniversalMappingField.KKS];
    if (typeof rawKks !== 'string') {
      return [];
    }
    return rawKks
      .split(DEFAULT_METADATA_VALUES_SEPARATOR)
      .map((kks) => kks.trim())
      .filter(Boolean);
  }

  function goToWorkspaceDetails(): void {
    router.push({
      name: Route.WORKSPACE_DETAILS,
      params: {
        [routeParams[Route.WORKSPACE_DETAILS].workspaceId]: props.workspaceId,
        [routeParams[Route.WORKSPACE_DETAILS].tab]:
          WorkspaceDetailsTab.DOCUMENTS,
      },
    });
  }
</script>

<style scoped lang="scss">
  .title-panel {
    margin-bottom: 40px;

    h3 {
      font-weight: 700;
      font-size: 36px;
      line-height: 36px;
    }
  }
</style>
